import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, Stack, Typography } from '@mui/material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'

import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import Modal from 'components/common/Modals/Modal/Modal'
import classnames from 'classnames'
import { logger } from 'utils/logging'
import popupStyles from '../TransferPropertiesPopup/TransferPropertiesPopup.module.sass'
import { useGetWorkspaceMembers, useReassignBroker } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  isOpen: boolean
  realtyIds: string[]
  onClose: () => void
  onSuccess: () => void
  /** Email of the broker currently assigned to the selected properties (only set when they all share one). Used to mark that broker as already assigned and block re-selecting it. */
  currentBrokerEmail?: string
}

interface BrokerOption {
  id: string
  name: string
  email: string
}

export const ReassignBrokerPopup: FC<Props> = ({
  isOpen,
  realtyIds,
  onClose,
  onSuccess,
  currentBrokerEmail,
}) => {
  const { t } = useTranslation(['properties_dashboard', 'actions'])
  const { userPropertyWorkspaceId } = useUserData()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const reassignMutation = useReassignBroker()

  const [selectedBroker, setSelectedBroker] = useState<BrokerOption | undefined>()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const membersQuery = useGetWorkspaceMembers(userPropertyWorkspaceId)

  useEffect(() => {
    if (!membersQuery.error) return
    logger.error('Failed to load brokers', membersQuery.error as Error, {
      context: { component: 'ReassignBrokerPopup', action: 'load_brokers' }
    })
    spawnErrorToast(t('bulk_actions.update_broker_error'))
    // excluded: t, spawnErrorToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersQuery.error])

  // Surface the silent empty-dropdown conditions (the ISSUE-1772 failure mode) so they are debuggable in production
  useEffect(() => {
    if (!isOpen) return

    if (!userPropertyWorkspaceId) {
      logger.warn('Reassign broker popup opened without a workspace id, broker list will be empty', undefined, {
        context: { component: 'ReassignBrokerPopup', action: 'load_brokers' }
      })
      return
    }

    if (membersQuery.isSuccess && (membersQuery.data?.length ?? 0) === 0) {
      logger.warn('Workspace returned no members available for broker reassignment', undefined, {
        context: { component: 'ReassignBrokerPopup', action: 'load_brokers' }
      })
    }
  }, [isOpen, userPropertyWorkspaceId, membersQuery.isSuccess, membersQuery.data])

  const brokersList = useMemo<BrokerOption[]>(() => {
    const members = membersQuery.data ?? []
    const normalizedQuery = searchQuery.trim().toLowerCase()
    return members
      // Fall back to email so members without a display name (e.g. invited-but-not-onboarded) are still identifiable, never blank
      .map(m => ({ id: m.userId, name: m.name?.trim() || m.email, email: m.email }))
      .filter(broker => !normalizedQuery || broker.name?.toLowerCase().includes(normalizedQuery))
  }, [membersQuery.data, searchQuery])

  const count = realtyIds.length

  const resetState = useCallback(() => {
    setSelectedBroker(undefined)
    setIsDropdownOpen(false)
    setSearchQuery('')
  }, [])

  const handleClose = useCallback(() => {
    onClose()
    reassignMutation.reset()
    resetState()
  }, [onClose, reassignMutation, resetState])

  const handleConfirm = useCallback(() => {
    if (!selectedBroker) return

    reassignMutation.mutate({
      workspaceId: userPropertyWorkspaceId,
      realtyIds,
      newBrokerId: selectedBroker.id,
    }, {
      onSuccess: () => {
        logAnalyticsEvent(AnalyticsEvent.REASSIGN_BROKER, { count, newBrokerId: selectedBroker.id })
        spawnSuccessToast(t('bulk_actions.update_broker_success', { count }))
        onSuccess()
        reassignMutation.reset()
        resetState()
      },
      onError: (error) => {
        logger.error('Reassign broker failed', error as Error, {
          context: { component: 'ReassignBrokerPopup', action: 'reassign_broker', metadata: { count, newBrokerId: selectedBroker.id } }
        })
        spawnErrorToast(t('bulk_actions.update_broker_error'))
      }
    })
  }, [reassignMutation, userPropertyWorkspaceId, realtyIds, selectedBroker, spawnSuccessToast, spawnErrorToast, t, count, onSuccess, resetState])

  const normalizedCurrentEmail = currentBrokerEmail?.trim().toLowerCase()
  const isCurrentBroker = useCallback(
    (email: string) => !!normalizedCurrentEmail && email.trim().toLowerCase() === normalizedCurrentEmail,
    [normalizedCurrentEmail]
  )

  const isPending = reassignMutation.isPending
  // Keep disabled until a broker different from the currently assigned one is picked
  const isConfirmDisabled = !selectedBroker || isPending || isCurrentBroker(selectedBroker.email)

  return (
    <Modal
      isOpen={isOpen}
      modalContentClassName={popupStyles.modal}
      onClickOutside={handleClose}
      onClose={handleClose}
      title={t('bulk_actions.update_broker_title')}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap={1}>

          <MUIButton
            type="secondaryBorder"
            disabled={isPending}
            onClick={handleClose}
          >
            {t('actions:Cancel')}
          </MUIButton>

          <MUIButton
            disabled={isConfirmDisabled}
            isLoading={isPending}
            onClick={handleConfirm}
          >
            {t('bulk_actions.update_broker')}
          </MUIButton>

        </Stack>
      }
    >
      <div className={classnames(popupStyles.inside)}>

        <Typography variant="text-md" color={GRAY_700} marginBottom="1rem">
          {t('bulk_actions.update_broker_description', { count })}
        </Typography>

        <Box marginBottom="1.2rem">
          <MUIDropdown
            open={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            placement="bottom-start"
            disablePortal={false}
            sx={{ zIndex: 101, '& .MuiPaper-root': { maxHeight: '20rem' } }}
            button={() => (
              <MUIDropdownInput
                type={DropdownInputTypes.SELECTOR}
                label={t('bulk_actions.update_broker_label')}
                value={selectedBroker?.name ?? searchQuery}
                placeholder={t('bulk_actions.update_broker_placeholder')}
                disabled={isPending}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setSelectedBroker(undefined)
                  if (!isDropdownOpen) setIsDropdownOpen(true)
                }}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />
            )}
          >
            {brokersList.map((broker) => {
              const isCurrent = isCurrentBroker(broker.email)

              return (
                <MUIDropdownItem
                  key={broker.id}
                  disabled={isCurrent}
                  isSelected={isCurrent || selectedBroker?.id === broker.id}
                  onClick={() => {
                    if (isCurrent) return
                    setSelectedBroker(broker)
                    setSearchQuery('')
                    setIsDropdownOpen(false)
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} width="100%">

                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                      {broker.name}
                    </Typography>

                    {isCurrent &&
                      <Typography variant="text-sm" color={GRAY_700}>
                        {t('bulk_actions.update_broker_current')}
                      </Typography>
                    }

                  </Stack>
                </MUIDropdownItem>
              )
            })}
          </MUIDropdown>
        </Box>

      </div>
    </Modal>
  )
}
